import { useEffect, type ReactNode } from 'react'
import { Menu } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { useTheme } from '@/hooks/useTheme'
import { useUIStore } from '@/stores/uiStore'
import { ThemeModeToggle } from '@/components/ui/ThemeModeToggle'
import { getExpandedSidebarLogoFallback } from '@/components/layout/sidebarLogos'
import { HEADER_BAND_HEIGHT_PX } from '@/constants/layout'
import { cn } from '@/utils/cn'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import { ParceirosSidebar } from './ParceirosSidebar'

type Props = {
  title: string
  children: ReactNode
}

export function ParceirosAppShell({ title, children }: Props) {
  const user = useAuthStore((s) => s.user)
  const { theme } = useThemeStore()
  const { isDark, toggleMode } = useTheme()
  const isMobile = useUIStore((s) => s.isMobile)
  const toggleMobileMenu = useUIStore((s) => s.toggleMobileMenu)
  const syncMobileViewport = useUIStore((s) => s.syncMobileViewport)
  const shellBorder = isDark ? 'border-white/[0.06]' : 'border-[#eef2f7]'
  const initials = (user?.name || 'P')
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  useEffect(() => {
    syncMobileViewport()
    const media = window.matchMedia('(max-width: 767px)')
    const onChange = () => syncMobileViewport()
    media.addEventListener('change', onChange)
    window.addEventListener('resize', onChange)
    return () => {
      media.removeEventListener('change', onChange)
      window.removeEventListener('resize', onChange)
    }
  }, [syncMobileViewport])

  return (
    <div className='flex h-screen w-full overflow-hidden bg-bg-dark font-sans'>
      <ParceirosSidebar />

      <main className='relative z-10 flex min-h-0 min-w-0 flex-1 flex-col bg-bg-main transition-all duration-300'>
        <header
          className={cn(
            'relative z-30 flex shrink-0 items-center justify-between gap-4 px-4 md:px-8',
            'bg-bg-header pt-[max(12px,env(safe-area-inset-top,0px))] md:border-b md:pt-0',
            shellBorder,
          )}
          style={{ minHeight: HEADER_BAND_HEIGHT_PX }}
        >
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            {isMobile ? (
              <>
                <button
                  type='button'
                  onClick={toggleMobileMenu}
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors',
                    isDark
                      ? 'text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
                      : 'text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#334155]',
                  )}
                  aria-label='Abrir menu'
                >
                  <Menu size={20} />
                </button>
                <div className='flex flex-1 justify-center'>
                  <img
                    src={getExpandedSidebarLogoFallback(theme.mode)}
                    alt={theme.companyName}
                    className='h-5 w-auto object-contain opacity-90'
                  />
                </div>
              </>
            ) : (
              <h1 className='min-w-0 truncate text-2xl font-bold tracking-tight text-text-primary'>
                {title}
              </h1>
            )}
          </div>

          <div className='ml-auto flex shrink-0 items-center gap-2'>
            <ThemeModeToggle isDark={isDark} onToggle={toggleMode} className='mb-0 py-0' />
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 text-[11px] font-bold text-white shadow-md',
                isDark ? 'border-white/[0.12] ring-1 ring-white/[0.08]' : 'border-white ring-1 ring-[#e2e8f0]',
              )}
              style={{ backgroundColor: theme.brandPrimary }}
              title={user?.name ?? 'Parceiro'}
            >
              {user?.avatarUrl ? (
                <img
                  src={resolvePublicAssetUrl(user.avatarUrl)}
                  alt={user.name}
                  className='h-full w-full object-cover'
                />
              ) : (
                initials
              )}
            </div>
          </div>
        </header>

        <section className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden'>
          <div className='mx-auto p-4 pb-20 md:max-w-[1600px] md:p-6 md:pb-6'>
            {isMobile ? (
              <h1 className='mb-4 text-base font-bold tracking-tight text-text-primary'>{title}</h1>
            ) : null}
            {children}
          </div>
        </section>
      </main>
    </div>
  )
}
